"use client";

import { Button } from "@beweegbaas/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@beweegbaas/ui/components/ui/dialog";
import { Input } from "@beweegbaas/ui/components/ui/input";
import { cn } from "@beweegbaas/ui/lib/utils";
import type { IconifySearchState } from "@gijsroge/iconify-search";
import { IconifySearchPrimitive } from "@gijsroge/iconify-search";
import { useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  IconArrowLeft,
  IconChevronRight,
  IconLoader,
  IconSearch,
  IconX,
} from "@tabler/icons-react";
import * as React from "react";
import { Activity } from "react";
import { Field } from "./ui/field";

const ICONS_PER_ROW = 8;
const ROW_HEIGHT = 40;
const HEADER_ROW_HEIGHT = 28;
const MAX_PACK_SUGGESTIONS = 6;
const ICONIFY_API = "https://api.iconify.design";

type IconifySearchContextValue = {
  disabled: boolean;
};

const IconifySearchContext = React.createContext<IconifySearchContextValue | null>(null);

function useIconifySearchContext(): IconifySearchContextValue {
  const ctx = React.useContext(IconifySearchContext);
  if (!ctx) throw new Error("useIconifySearchContext must be used within IconifySearch");
  return ctx;
}

type IconifyCollectionInfo = {
  name: string;
  total: number;
};

type IconifyCollectionsResponse = Record<string, IconifyCollectionInfo>;

type IconifyCollectionResponse = {
  prefix: string;
  total: number;
  title?: string;
  uncategorized?: string[];
  categories?: Record<string, string[]>;
};

/** All icon packs known to Iconify, keyed by prefix. Cached for the session. */
function useIconifyCollections() {
  return useQuery({
    queryKey: ["iconify-collections"],
    queryFn: async (): Promise<IconifyCollectionsResponse> => {
      const response = await fetch(`${ICONIFY_API}/collections`);
      if (!response.ok) throw new Error(`Iconify API error: ${response.status}`);
      return response.json() as Promise<IconifyCollectionsResponse>;
    },
    staleTime: Infinity,
  });
}

/** Full icon list of a single pack (grouped by category when available). */
function useIconifyCollection(prefix: string | null) {
  return useQuery({
    queryKey: ["iconify-collection", prefix],
    enabled: prefix !== null,
    queryFn: async (): Promise<IconifyCollectionResponse> => {
      const params = new URLSearchParams({ prefix: prefix ?? "" });
      const response = await fetch(`${ICONIFY_API}/collection?${params}`);
      if (!response.ok) throw new Error(`Iconify API error: ${response.status}`);
      return response.json() as Promise<IconifyCollectionResponse>;
    },
    staleTime: 1000 * 60 * 5,
  });
}

type PackFilter = {
  prefix: string;
  name: string;
};

type IconGroup = {
  /** Unique key for this group (collection prefix or pack category) */
  key: string;
  /** Header label; null renders the group without a header row */
  name: string | null;
  /** Set when clicking the header should open that pack */
  pack?: PackFilter;
  icons: string[];
};

type VirtualRow =
  | { type: "header"; name: string; pack?: PackFilter; key: string }
  | { type: "icons"; iconIds: string[]; key: string };

function flattenGroupsToRows(groups: IconGroup[]): VirtualRow[] {
  const rows: VirtualRow[] = [];
  for (const { key, name, pack, icons } of groups) {
    if (name !== null) {
      rows.push({ type: "header", name, pack, key: `h-${key}` });
    }
    for (let i = 0; i < icons.length; i += ICONS_PER_ROW) {
      rows.push({
        type: "icons",
        iconIds: icons.slice(i, i + ICONS_PER_ROW),
        key: `${key}-${i}`,
      });
    }
  }
  return rows;
}

const IconCell = React.memo(function IconCell({
  iconId,
  isSelected,
  selectIcon,
  onClose,
  multiple,
  getIconUrl,
}: {
  iconId: string;
  isSelected: boolean;
  selectIcon: (iconId: string) => void;
  onClose: () => void;
  multiple: boolean;
  getIconUrl: (iconId: string, size?: number) => string;
}) {
  const { disabled } = useIconifySearchContext();
  const onSelect = React.useCallback(() => {
    if (disabled) return;
    selectIcon(iconId);
    if (!multiple) onClose();
  }, [selectIcon, iconId, multiple, onClose, disabled]);

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      title={iconId.split(":")[1] ?? iconId}
      className={cn(
        `hover:bg-accent flex size-10 shrink-0 items-center justify-center
        rounded-md transition-colors`,
        isSelected && "bg-accent",
      )}
    >
      <img
        src={getIconUrl(iconId, 24)}
        alt={iconId.split(":")[1] ?? iconId}
        className="size-6"
        width={24}
        height={24}
      />
    </button>
  );
});

function VirtualizedIconList({
  groups,
  selectedIcons,
  selectIcon,
  multiple,
  getIconUrl,
  onClose,
  onPackOpen,
}: {
  groups: IconGroup[];
  selectedIcons: string[];
  selectIcon: (iconId: string) => void;
  multiple: boolean;
  getIconUrl: (iconId: string, size?: number) => string;
  onClose: () => void;
  onPackOpen?: (pack: PackFilter) => void;
}) {
  const parentRef = React.useRef<HTMLDivElement>(null);
  const rows = React.useMemo(() => flattenGroupsToRows(groups), [groups]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => (rows[index]?.type === "header" ? HEADER_ROW_HEIGHT : ROW_HEIGHT),
    overscan: 4,
  });

  const selectedSet = React.useMemo(() => new Set(selectedIcons), [selectedIcons]);

  return (
    <div
      ref={parentRef}
      className="scroll-fade-y min-h-0 flex-1 overflow-y-auto"
      style={{ contain: "strict" }}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const row = rows[virtualRow.index];
          if (row?.type === "header") {
            return (
              <div
                key={row.key}
                className="text-muted-foreground absolute top-0 left-0 flex
                  w-full items-center px-2 text-xs font-medium backdrop-blur
                  sm:px-0"
                style={{
                  height: HEADER_ROW_HEIGHT,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {row.pack && onPackOpen ? (
                  <button
                    type="button"
                    onClick={() => onPackOpen(row.pack as PackFilter)}
                    title={`Bekijk alle iconen in ${row.name}`}
                    className="hover:text-foreground group flex items-center
                      gap-0.5 transition-colors"
                  >
                    {row.name}
                    <IconChevronRight
                      className="size-3.5 transition-transform
                        group-hover:translate-x-0.5"
                    />
                  </button>
                ) : (
                  row.name
                )}
              </div>
            );
          }
          return (
            <div
              key={row?.key}
              className="absolute left-0 flex w-full flex-wrap gap-1"
              style={{
                height: ROW_HEIGHT,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {row?.iconIds.map((iconId) => (
                <IconCell
                  key={iconId}
                  iconId={iconId}
                  isSelected={selectedSet.has(iconId)}
                  selectIcon={selectIcon}
                  onClose={onClose}
                  multiple={multiple}
                  getIconUrl={getIconUrl}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Clickable pack suggestions shown when the query matches pack names. */
function PackSuggestions({
  query,
  onPackOpen,
}: {
  query: string;
  onPackOpen: (pack: PackFilter) => void;
}) {
  const { disabled } = useIconifySearchContext();
  const { data: collections } = useIconifyCollections();

  const matches = React.useMemo(() => {
    if (!collections) return [];
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    return Object.entries(collections)
      .filter(
        ([prefix, info]) =>
          prefix.toLowerCase().includes(needle) || info.name.toLowerCase().includes(needle),
      )
      .slice(0, MAX_PACK_SUGGESTIONS);
  }, [collections, query]);

  if (matches.length === 0) return null;

  return (
    <div className="flex flex-col shrink-0 flex-wrap items-start gap-1.5">
      <span className="text-muted-foreground text-xs">Packs</span>
      <div className="flex flex-wrap gap-1.5">
        {matches.map(([prefix, info]) => (
          <Button
            key={prefix}
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            className="h-6 rounded-full px-2.5 text-xs"
            onClick={() => onPackOpen({ prefix, name: info.name })}
          >
            {info.name}
            <span className="text-muted-foreground ml-1 text-[10px]">{info.total}</span>
          </Button>
        ))}
      </div>
    </div>
  );
}

/** Browse view for a single pack: full icon list with local name filtering. */
function PackBrowser({
  pack,
  state,
  setOpen,
  onClearPack,
}: {
  pack: PackFilter;
  state: IconifySearchState;
  setOpen: (open: boolean) => void;
  onClearPack: () => void;
}) {
  const { disabled } = useIconifySearchContext();
  const [packQuery, setPackQuery] = React.useState("");
  const onClose = React.useCallback(() => setOpen(false), [setOpen]);
  const { data, isLoading, isError } = useIconifyCollection(pack.prefix);

  const groups = React.useMemo((): IconGroup[] => {
    if (!data) return [];
    const needle = packQuery.trim().toLowerCase();
    const matchesQuery = (name: string) => !needle || name.toLowerCase().includes(needle);
    const toIconIds = (names: string[]) =>
      names.filter(matchesQuery).map((name) => `${pack.prefix}:${name}`);

    const result: IconGroup[] = [];
    const categories = Object.entries(data.categories ?? {});
    for (const [category, names] of categories) {
      const icons = toIconIds(names);
      if (icons.length > 0) {
        result.push({ key: `${pack.prefix}/${category}`, name: category, icons });
      }
    }
    const uncategorized = toIconIds(data.uncategorized ?? []);
    if (uncategorized.length > 0) {
      result.push({
        key: `${pack.prefix}/uncategorized`,
        // No header when the pack has no categories at all
        name: categories.length > 0 ? "Overig" : null,
        icons: uncategorized,
      });
    }
    return result;
  }, [data, packQuery, pack.prefix]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex shrink-0 flex-col items-start gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          className="text-muted-foreground hover:text-foreground -ml-2 h-7
            gap-1 px-2 text-xs"
          onClick={onClearPack}
        >
          <IconArrowLeft className="size-3.5" />
          Terug naar zoeken
        </Button>
        <Field className="w-full">
          <Input
            placeholder="Filter iconen in deze pack..."
            value={packQuery}
            onChange={(e) => setPackQuery(e.target.value)}
            disabled={disabled}
          />
        </Field>
      </div>
      {isLoading ? (
        <div className="scroll-fade-y min-h-0 flex-1">
          <p className="text-muted-foreground py-8 text-center text-sm">Pack laden...</p>
        </div>
      ) : isError ? (
        <div className="scroll-fade-y min-h-0 flex-1">
          <p className="text-muted-foreground py-8 text-center text-sm">
            Pack kon niet geladen worden. Probeer het opnieuw.
          </p>
        </div>
      ) : groups.length === 0 ? (
        <div className="scroll-fade-y min-h-0 flex-1">
          <p className="text-muted-foreground py-8 text-center text-sm">
            Geen iconen gevonden in deze pack.
          </p>
        </div>
      ) : (
        <VirtualizedIconList
          groups={groups}
          selectedIcons={state.selectedIcons}
          selectIcon={state.selectIcon}
          multiple={state.multiple}
          getIconUrl={state.getIconUrl}
          onClose={onClose}
        />
      )}
    </div>
  );
}

function GlobalSearch({
  state,
  setOpen,
  onPackOpen,
}: {
  state: IconifySearchState;
  setOpen: (open: boolean) => void;
  onPackOpen: (pack: PackFilter) => void;
}) {
  const { disabled } = useIconifySearchContext();
  const onClose = React.useCallback(() => setOpen(false), [setOpen]);

  const groups = React.useMemo(
    (): IconGroup[] =>
      state.groups.map((group) => ({
        key: group.prefix,
        name: group.name,
        pack: { prefix: group.prefix, name: group.name },
        icons: group.icons,
      })),
    [state.groups],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="relative shrink-0">
        <Field>
          <Input
            placeholder="Zoek iconen of packs..."
            value={state.query}
            onChange={(e) => state.setQuery(e.target.value)}
            disabled={disabled}
            className="pr-9"
          />
        </Field>
        {state.isPending && (
          <IconLoader
            className="text-muted-foreground absolute top-1/2 right-3 size-4
              -translate-y-1/2 animate-spin"
            aria-hidden
          />
        )}
      </div>
      <PackSuggestions query={state.debouncedQuery} onPackOpen={onPackOpen} />
      {!state.debouncedQuery.trim() ? (
        <div className="scroll-fade-y min-h-0 flex-1">
          <p className="text-muted-foreground py-8 text-center text-sm">
            Typ om iconen of packs te zoeken (gebruik engelse termen)
          </p>
        </div>
      ) : state.isPending && !state.data ? (
        <div className="scroll-fade-y min-h-0 flex-1">
          <p className="text-muted-foreground py-8 text-center text-sm">Zoeken...</p>
        </div>
      ) : state.groups.length === 0 ? (
        <div className="scroll-fade-y min-h-0 flex-1">
          <p className="text-muted-foreground py-8 text-center text-sm">
            Geen iconen gevonden. Probeer een andere zoekopdracht (gebruik engelse termen).
          </p>
        </div>
      ) : (
        <VirtualizedIconList
          groups={groups}
          selectedIcons={state.selectedIcons}
          selectIcon={state.selectIcon}
          multiple={state.multiple}
          getIconUrl={state.getIconUrl}
          onClose={onClose}
          onPackOpen={onPackOpen}
        />
      )}
    </div>
  );
}

function IconifySearchUI({
  state,
  open,
  setOpen,
}: {
  state: IconifySearchState;
  open: boolean;
  setOpen: (open: boolean) => void;
}) {
  const { disabled } = useIconifySearchContext();
  const [packFilter, setPackFilter] = React.useState<PackFilter | null>(null);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      // Reset to global search whenever the dialog closes
      if (!nextOpen) setPackFilter(null);
    },
    [setOpen],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <div className="relative inline-flex">
        <DialogTrigger
          render={
            <Button
              variant="secondary"
              disabled={disabled}
              className={cn(state.selectedIcons.length > 0 && "pr-8")}
            />
          }
        >
          {state.selectedIcons.length > 0 ? (
            <span className="flex items-center gap-2">
              {state.selectedIcons.slice(0, 3).map((iconId) => (
                <img
                  key={iconId}
                  src={state.getIconUrl(iconId, 18)}
                  alt=""
                  className="size-4"
                  width={18}
                  height={18}
                />
              ))}
              {state.selectedIcons.length > 3 && (
                <span className="text-muted-foreground text-xs">
                  +{state.selectedIcons.length - 3}
                </span>
              )}
              {!state.multiple && state.selectedIcons[0] && (
                <span>{state.selectedIcons[0].split(":")[1] ?? state.selectedIcons[0]}</span>
              )}
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <IconSearch className="size-4" />
              {state.multiple ? "Kies iconen" : "Kies icoon"}
            </span>
          )}
        </DialogTrigger>
        {state.selectedIcons.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            className="group text-foreground/50 hover:text-foreground
              absolute top-px right-px size-8 max-h-[calc(100%-2px)]
              shrink-0 bg-transparent!"
            onClick={() => state.setSelectedIcons([])}
            aria-label="Wis selectie"
          >
            <IconX className="size-4 scale-80 transition-all group-hover:scale-90" />
          </Button>
        )}
      </div>
      <DialogContent className="flex h-[min(720px,85vh)] flex-col gap-4 sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{packFilter ? packFilter.name : "Zoek iconen"}</DialogTitle>
        </DialogHeader>
        {/* Hidden (not unmounted) while browsing a pack so search results,
            virtualizer state and scroll position survive the drill-down */}
        <Activity mode={packFilter ? "hidden" : "visible"}>
          <GlobalSearch state={state} setOpen={handleOpenChange} onPackOpen={setPackFilter} />
        </Activity>
        {packFilter ? (
          <PackBrowser
            key={packFilter.prefix}
            pack={packFilter}
            state={state}
            setOpen={handleOpenChange}
            onClearPack={() => setPackFilter(null)}
          />
        ) : null}
        {state.multiple && state.selectedIcons.length > 0 && (
          <div
            className="-mx-6 flex items-center justify-between gap-4 border-t
              px-6 pt-2"
          >
            <div className="relative min-w-0 flex-1 overflow-hidden">
              <div
                className="scrollbar-width:none flex gap-1 overflow-x-auto pb-1
                  [&::-webkit-scrollbar]:hidden"
                style={{
                  maskImage: "linear-gradient(to right, black 70%, transparent 100%)",
                  WebkitMaskImage: "linear-gradient(to right, black 70%, transparent 100%)",
                }}
              >
                {state.selectedIcons.map((iconId) => (
                  <img
                    key={iconId}
                    src={state.getIconUrl(iconId, 24)}
                    alt={iconId.split(":")[1] ?? iconId}
                    className="size-6 shrink-0 rounded"
                    width={16}
                    height={16}
                  />
                ))}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              disabled={disabled}
              className="text-muted-foreground hover:text-foreground shrink-0"
              onClick={() => state.setSelectedIcons([])}
            >
              Alle wissen
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export interface IconifySearchProps {
  /** Allow selecting multiple icons */
  multiple?: boolean;
  /** Disable the trigger, clear button, search input, and actions */
  disabled?: boolean;
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
  searchValue?: string;
  defaultSearchValue?: string;
  onSearchChange?: (value: string) => void;
}

export function IconifySearch({
  multiple,
  disabled,
  value,
  defaultValue,
  onValueChange,
  searchValue,
  defaultSearchValue,
  onSearchChange,
}: IconifySearchProps): React.ReactElement | null {
  const [open, setOpen] = React.useState(false);

  return (
    <IconifySearchPrimitive
      debounceMs={300}
      multiple={multiple}
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      searchValue={searchValue}
      defaultSearchValue={defaultSearchValue}
      onSearchChange={onSearchChange}
    >
      {(state) => (
        <IconifySearchContext.Provider value={{ disabled: disabled ?? false }}>
          <IconifySearchUI state={state} open={open} setOpen={setOpen} />
        </IconifySearchContext.Provider>
      )}
    </IconifySearchPrimitive>
  );
}
